import { FieldOption, FieldType } from '../../models/field-definition';
import { AnnotationSpeaker } from '../../models/StreamingModels';

/** The parts of a form field (live definition or saved snapshot entry) that can name a speaker. */
export interface SpeakerSourceField {
  label: string;
  type: FieldType;
  referenceField?: string;
  options?: FieldOption[];
}

// Reference roles that hold case data rather than a person, compared with spaces/underscores
// stripped and lower-cased. These fields can be plain text inputs, so the type check misses them.
const NON_SPEAKER_REFERENCE_ROLES = new Set([
  'hearingdate', 'hearingdatetime', 'casenotes', 'notes'
]);

/**
 * Every field linked to a referenceField (Judge, Defendant, ...) that holds a person's name becomes
 * a speaker. Date and checkbox fields, and non-person references such as HearingDate and Case Notes,
 * never name a person, so they are skipped.
 */
export function buildAnnotationSpeakers<T extends SpeakerSourceField>(
  fields: T[],
  valueOf: (field: T) => unknown
): AnnotationSpeaker[] {
  const speakers: AnnotationSpeaker[] = [];
  for (const field of fields) {
    const role = field.referenceField?.trim();
    if (!role || field.type === FieldType.Date || field.type === FieldType.DateTime || field.type === FieldType.Checkbox) {
      continue;
    }
    if (NON_SPEAKER_REFERENCE_ROLES.has(role.replace(/[\s_]+/g, '').toLowerCase())) {
      continue;
    }
    const raw = valueOf(field);
    const option = field.type === FieldType.Dropdown
      ? field.options?.find(o => String(o.value) === String(raw))
      : undefined;
    const name = String(option?.label ?? raw ?? '').trim();
    // A "None" dropdown option, or a null that was stringified on save, means nobody is assigned.
    if (!name || ['none', 'null', 'undefined'].includes(name.toLowerCase())) {
      continue;
    }
    if (!speakers.some(s => s.name.toLowerCase() === name.toLowerCase())) {
      speakers.push({ role, label: field.label || role, name });
    }
  }
  return speakers;
}
